import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";
import { Tooltip, Box, CircularProgress, Typography, Chip, Alert, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, Autocomplete, AutocompleteRenderInputParams, alpha, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { MerchantType } from "../../../../interfaces/delivery-company/merchants";
import Decimal from "decimal.js";
import { IArea } from "../../../../interfaces/common/location";
import { useSnackBar } from "../../../../contexts/snackbar";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import { AuthContext } from "../../../../contexts/user-context/user-context";
import axios from "axios";
import { geoCentroid } from "d3-geo";
import { getGovernorateForDistrict, lebaneseGovernorates, regions } from "../../../../data/regions";
import Grid2 from "@mui/material/Unstable_Grid2";
import { red } from "@mui/material/colors";
import { rules } from "eslint-plugin-prettier";
import { KeyboardArrowDown, KeyboardArrowUp, ShopOutlined } from "@mui/icons-material";
import { IDeliveryChargeRule, DeliveryChargeRuleScope } from "./interfaces";
import DeliveryRulesGraph from "./delivery-rules-graph";
import DeliveryZoneAutocomplete from "./delivery-zone-autocomplete";
import { OrgSettingsContext } from "../../../../contexts/org-settings-context";
import { IconWithText } from "../../../common/styles/components";
import { MerchantWithIcon } from "../../../common/styles/merchant-with-icon";
import ConfirmAction from "../../../common/confirm-action/confirm-action";
import DeliveryChargeFormDialog from "./delivery-charge-rule-form";
import { DataResources, ResourceActionType, user_has_data_model_access } from "../../../../utils/permissions";

// 🌍 Local TopoJSON path
const LEBANON_TOPOJSON = "/lebanon-topo.json";

interface DeliveryChargeOverridesProps {
  merchant: MerchantType;
  defaultFocus?: IDeliveryChargeRule
  readonly?:boolean
}

const getFillColor = () => {
  return "#EEEEEE";
};

export type highlightZoneFunc = (scope: DeliveryChargeRuleScope.DISTRICT | DeliveryChargeRuleScope.GOVERNORATE, name: string) => void

const DeliveryChargeOverrides: FunctionComponent<DeliveryChargeOverridesProps> = ({
  merchant,
  defaultFocus,
  readonly = false,
}) => {
  const [rules, setRules] = useState<IDeliveryChargeRule[]>();
  const [isLoading, setIsLoading] = useState(false);
  const { showMessage } = useSnackBar();
  const { config } = useContext(AuthContext);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>()
  const [focus, setFocus] = useState<IDeliveryChargeRule | undefined>(defaultFocus)
  const {color} = useContext(OrgSettingsContext)
  const highlightZone: highlightZoneFunc = (scope: DeliveryChargeRuleScope.DISTRICT | DeliveryChargeRuleScope.GOVERNORATE, name: string) => {
    if (scope == DeliveryChargeRuleScope.DISTRICT) setSelectedDistricts([name])
    else setSelectedDistricts(regions.filter(item=>item.governorate == name).map(item=>item.district))
  }

  const {user} = useContext(AuthContext)

  const [ruleFormDialogOpen, setRuleFormDialogOpen] = useState<IDeliveryChargeRule>()

  useEffect(()=>{
    if (focus) {
      if (focus?.scope == DeliveryChargeRuleScope.DISTRICT) highlightZone(focus.scope, focus.district!)
      if (focus?.scope == DeliveryChargeRuleScope.GOVERNORATE) highlightZone(focus.scope, getGovernorateForDistrict(focus.district!))
      if (focus?.scope == DeliveryChargeRuleScope.AREA) highlightZone(DeliveryChargeRuleScope.DISTRICT, focus.area!.district) 
    } else highlightZone(DeliveryChargeRuleScope.GOVERNORATE, '')
  }, [focus])

  
  const fetchRules = async () => {
    setIsLoading(true);
    const endpoint = `${API_ENDPOINT}/v1/delivery-fee-rules/?merchant=${merchant.id}`;

    try {
      const response = await axios.get<IDeliveryChargeRule[]>(endpoint, config);
      const parsed = response.data.map((r) => ({
        ...r,
        delta_charge: new Decimal(r.delta_charge),
      }));
      setRules(parsed);
    } catch {
      showMessage("Failed to load rules", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const resolveRuleForGovernorate = (governorateName: string): IDeliveryChargeRule | undefined => {
  if (!rules) return undefined;
  return rules.find(
    (rule) =>
      rule.scope === DeliveryChargeRuleScope.GOVERNORATE &&
      rule.governorate?.toLowerCase() === governorateName.toLowerCase()
  );
};


  return !isLoading && rules === undefined ? (
    <Alert severity="error">
      Something went wrong. Please try again in a few seconds, or contact us if
      the issue persists.
    </Alert>
  ) : (
    <Box width={"100%"} pt={4} sx={{background: `linear-gradient(to bottom,rgb(255, 255, 255), ${alpha(color, 0.5)})` }}> 
      <Box display={"flex"} justifyContent={"center"}>
         <DeliveryZoneAutocomplete 
                  rules={rules ?? []} 
                  focus={focus} 
                  setFocus={setFocus} 
                  merchant={merchant} 
                  refetchRules={fetchRules}
                  readonly={readonly}
          />
      </Box>
      
        <Grid2 container spacing={0}> 
            <Grid2 xs={12} md={10}> <Box>
                <DeliveryRulesGraph 
                  readonly={readonly}
                  isLoading={isLoading} 
                  rules={rules ?? []} 
                  highlightZone={highlightZone} 
                  focus={focus} 
                  merchant={merchant}
                  setFocus={setFocus}
                  canDelete={user_has_data_model_access(user, DataResources.DELIVERY_FEE_RULES, ResourceActionType.DELETE)}
                  canAdd={user_has_data_model_access(user, DataResources.DELIVERY_FEE_RULES, ResourceActionType.CREATE)}
                  canEdit={user_has_data_model_access(user, DataResources.DELIVERY_FEE_RULES, ResourceActionType.UPDATE)}
                  editRule={(r: IDeliveryChargeRule)=>setRuleFormDialogOpen(r)}
                />
            </Box>
        
        </Grid2>
             <Grid2 xs={12} md={2} display={"flex"} justifyContent={"flex-start"}  justifyItems={"center"}> 
                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ scale: 10000, center: [35.89, 33.9] }}
                    width={300}
                    height={500}
                >
                    <Geographies geography={LEBANON_TOPOJSON}>
                    {({ geographies }) =>
                        geographies.map((geo) => {
                        const district = geo.properties.NAME || geo.properties.name || "Unknown";
                        const rule = resolveRuleForGovernorate(district);
                        const delta = rule?.delta_charge;
                        const fillColor = getFillColor();


                        return (
                            <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={selectedDistricts?.includes(district) ? red[200] : fillColor}
                            stroke="#555"
                            style={{
                                default: { outline: "none" },
                                hover: { outline: "none", cursor: "pointer", fill: "#B0BEC5" },
                                pressed: { outline: "none" },
                            }}
                            />
                        );
                        })
                    }
                    </Geographies>
                </ComposableMap>
        </Grid2>
           
           

        </Grid2>
        {
          ruleFormDialogOpen && <DeliveryChargeFormDialog 
            open={true} 
            rule={ruleFormDialogOpen} 
            onUpdateSuccess={
              () => {
                showMessage("Updated successfully")
                fetchRules()
              }
            } 
            onDeleteSuccess={
               () => {
                showMessage("Deleted successfully")
                fetchRules()
              }
            }
            onClose={
              () => setRuleFormDialogOpen(undefined)
            } 
            merchant={merchant} />
        }


    </Box>
  );
};

export default DeliveryChargeOverrides;
